import React, { useState, useEffect } from 'react';
import {
    X,
    Edit2,
    Trash2,
    ChevronLeft,
    ChevronRight,
    Copy,
    CheckCircle2,
    Eye,
    EyeOff,
    Shield,
    User,
    Globe,
    Database,
    Monitor,
    MapPin,
    FileText,
    Tag,
    Clock,
    Hash,
    Server,
    AlertTriangle,
    Loader2,
    Info,
    Terminal,
    Key
} from 'lucide-react';
import { ThreatIntelRecordDetail, SeverityType, StatusType } from '@/lib/api/types';
import { useUnmaskSavedPasswordMutation } from '@/lib/services/unmaskApi';

// Badge Components
interface SeverityBadgeProps {
    severity: SeverityType;
}

interface StatusBadgeProps {
    status: StatusType;
}

interface DataTypeBadgeProps {
    dataType?: string | null;
}

interface SourceBadgeProps {
    source?: string | null;
}

const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
    const getConfig = (severity: SeverityType) => {
        switch (severity) {
            case 'critical':
                return 'bg-[#FF3333]/10 text-[#FF3333] border-[#FF3333]/30';
            case 'high':
                return 'bg-[#F97316]/10 text-[#F97316] border-[#F97316]/30';
            case 'medium':
                return 'bg-[#EAB308]/10 text-[#EAB308] border-[#EAB308]/30';
            case 'low':
                return 'bg-[#50A970]/10 text-[#50A970] border-[#50A970]/30';
            default:
                return 'bg-slate-500/10 text-slate-500 border-slate-500/30';
        }
    };

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border ${getConfig(severity)}`}>
            <Shield className="h-3 w-3" />
            {severity}
        </span>
    );
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
    const getConfig = (status: StatusType) => {
        switch (status) {
            case 'new':
                return 'bg-[#00FFFF]/10 text-[#00FFFF] border-[#00FFFF]/30';
            case 'investigating':
                return 'bg-[#EAB308]/10 text-[#EAB308] border-[#EAB308]/30';
            case 'resolved':
                return 'bg-[#50A970]/10 text-[#50A970] border-[#50A970]/30';
            case 'false_positive':
                return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
            default:
                return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
        }
    };

    const displayText = status === 'false_positive' ? 'False Positive' : status;

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border ${getConfig(status)}`}>
            <Tag className="h-3 w-3" />
            {displayText}
        </span>
    );
};

const DataTypeBadge: React.FC<DataTypeBadgeProps> = ({ dataType }) => {
    if (!dataType) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border bg-slate-500/10 text-slate-400 border-slate-500/30">
                <Database className="h-3 w-3" />
                UNKNOWN
            </span>
        );
    }

    const getConfig = (type: string) => {
        const normalizedType = type.toLowerCase();
        switch (normalizedType) {
            case 'stealer':
                return { classes: 'bg-[#FF3333]/10 text-[#FF3333] border-[#FF3333]/30', icon: <Shield className="h-3 w-3" /> };
            case 'credential':
            case 'credentials':
                return { classes: 'bg-[#4da760]/10 text-[#4da760] border-[#4da760]/30', icon: <User className="h-3 w-3" /> };
            case 'combolist':
                return { classes: 'bg-[#00FFFF]/10 text-[#00FFFF] border-[#00FFFF]/30', icon: <Database className="h-3 w-3" /> };
            default:
                return { classes: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: <Database className="h-3 w-3" /> };
        }
    };

    const config = getConfig(dataType);
    const displayText = dataType.replace(/_/g, ' ');

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border ${config.classes}`}>
            {config.icon}
            {displayText}
        </span>
    );
};

const SourceBadge: React.FC<SourceBadgeProps> = ({ source }) => {
    if (!source) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border bg-slate-500/10 text-slate-400 border-slate-500/30">
                <Database className="h-3 w-3" />
                UNKNOWN
            </span>
        );
    }

    const getConfig = (src: string) => {
        const normalizedSource = src.toLowerCase();
        switch (normalizedSource) {
            case 'consumer':
                return { classes: 'bg-[#00FFFF]/10 text-[#00FFFF] border-[#00FFFF]/30', icon: <User className="h-3 w-3" /> };
            case 'corporate':
                return { classes: 'bg-[#4da760]/10 text-[#4da760] border-[#4da760]/30', icon: <Shield className="h-3 w-3" /> };
            case 'stealer':
                return { classes: 'bg-[#FF3333]/10 text-[#FF3333] border-[#FF3333]/30', icon: <Shield className="h-3 w-3" /> };
            case 'breach':
                return { classes: 'bg-[#F97316]/10 text-[#F97316] border-[#F97316]/30', icon: <Database className="h-3 w-3" /> };
            default:
                return { classes: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: <Database className="h-3 w-3" /> };
        }
    };

    const config = getConfig(source);

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border ${config.classes}`}>
            {config.icon}
            {source}
        </span>
    );
};

// DetailRow Component with Enhanced Unmask Support
interface DetailRowProps {
    label: string;
    value?: string | null;
    icon?: React.ReactNode;
    copyable?: boolean;
    sensitive?: boolean;
    fieldKey?: string;
    recordId?: number;
    logId?: number | null;
    isPassword?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({
    label, value, icon, copyable = false, sensitive = false, fieldKey, recordId, logId, isPassword = false
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [unmaskedPassword, setUnmaskedPassword] = useState<string | null>(null);
    const [unmaskError, setUnmaskError] = useState<string | null>(null);
    const [remainingUnmasks, setRemainingUnmasks] = useState<number | null>(null);
    const [unmaskSource, setUnmaskSource] = useState<string | null>(null);
    const [wasAlreadyUnmasked, setWasAlreadyUnmasked] = useState<boolean>(false);

    const [unmaskSavedPassword, { isLoading: isUnmasking }] = useUnmaskSavedPasswordMutation();

    // Check if unmasking is available for this record
    const canUnmask = isPassword && recordId && logId;

    const copyToClipboard = async (text: string, field?: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field || null);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    const handleToggleVisibility = async () => {
        if (isPassword && canUnmask) {
            if (!isVisible && !unmaskedPassword) {
                await handleUnmaskPassword();
            } else {
                setIsVisible(!isVisible);
            }
        } else {
            setIsVisible(!isVisible);
        }
    };

    const handleUnmaskPassword = async () => {
        if (!recordId || !isPassword) {
            setUnmaskError('Cannot unmask: Missing record information');
            return;
        }

        if (!logId) {
            setUnmaskError('Cannot unmask: This record type does not support password unmasking');
            return;
        }

        setUnmaskError(null);
        try {
            const response = await unmaskSavedPassword({
                record_id: recordId,
                log_id: logId
            }).unwrap();

            setUnmaskedPassword(response.password);
            setRemainingUnmasks(response.unmasks_remaining);
            setUnmaskSource(response.source);
            setWasAlreadyUnmasked(response.previously_unmasked);
            setIsVisible(true);
        } catch (error: any) {
            console.error('Failed to unmask password:', error);
            let errorMessage = 'Failed to unmask password. Please try again.';

            if (error.message) {
                errorMessage = error.message;
            } else if (error.status === 403) {
                errorMessage = 'Unmask quota exceeded or not supported in your plan';
            } else if (error.status === 404) {
                errorMessage = 'Record not found';
            } else if (error.status === 422) {
                errorMessage = 'This record type does not support password unmasking';
            } else if (error.data?.error) {
                errorMessage = error.data.error;
            }

            setUnmaskError(errorMessage);
        }
    };

    if (!value) {
        return (
            <div className="space-y-1 py-3 border-b border-[#1A1D24] group">
                <div className="flex items-center gap-2 mb-1">
                    <div className="text-slate-500">{icon}</div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</div>
                </div>
                <div className="pl-6 text-[13px] text-slate-600 font-mono">-</div>
            </div>
        );
    }

    const actualValue = isPassword && unmaskedPassword ? unmaskedPassword : value;
    const displayValue = sensitive && !isVisible ? '•'.repeat(Math.min(actualValue.length, 20)) : actualValue;

    const getEyeIconAndTooltip = () => {
        if (isPassword) {
            if (!canUnmask) {
                return { icon: <Eye className="h-3.5 w-3.5 opacity-30" />, tooltip: "Password unmasking not available for this record type", loading: false, disabled: true };
            }
            if (!unmaskedPassword && !isVisible) {
                return { icon: <Eye className="h-3.5 w-3.5" />, tooltip: "Unmask password (uses quota)", loading: isUnmasking, disabled: false };
            } else {
                return { icon: isVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />, tooltip: isVisible ? "Hide password" : "Show password", loading: false, disabled: false };
            }
        } else {
            return { icon: isVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />, tooltip: isVisible ? "Hide" : "Show", loading: false, disabled: false };
        }
    };

    const eyeConfig = getEyeIconAndTooltip();

    return (
        <div className="space-y-1 py-3 border-b border-[#1A1D24] group flex flex-col">
            <div className="flex items-center gap-2 mb-1">
                <div className="text-slate-500">{icon}</div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</div>
            </div>

            <div className="flex items-center justify-between pl-6">
                <div className="text-[13px] text-slate-300 font-mono break-all transition-colors group-hover:text-white" title={actualValue}>
                    {displayValue}
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {sensitive && (
                        <button
                            onClick={handleToggleVisibility}
                            disabled={eyeConfig.loading || eyeConfig.disabled}
                            className="p-1.5 text-slate-400 hover:text-[#00FFFF] hover:bg-[#161A23] rounded transition-all disabled:opacity-50"
                            title={eyeConfig.tooltip}
                        >
                            {eyeConfig.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#00FFFF]" /> : eyeConfig.icon}
                        </button>
                    )}
                    {copyable && (
                        <button
                            onClick={() => copyToClipboard(actualValue, fieldKey)}
                            className="p-1.5 text-slate-400 hover:text-[#50A970] hover:bg-[#161A23] rounded transition-all"
                            title="Copy to clipboard"
                        >
                            {copiedField === fieldKey ? <CheckCircle2 className="h-3.5 w-3.5 text-[#50A970]" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                    )}
                </div>
            </div>

            {/* Injected Cyber Terminal output for errors / success */}
            {isPassword && (unmaskError || unmaskedPassword) && (
                <div className="mt-3 pl-6">
                    <div className="bg-[#050505] border border-[#1A1D24] rounded-lg p-3 space-y-2">
                        <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1 flex items-center gap-2">
                            <Terminal className="h-3 w-3" /> Terminal Output
                        </div>
                        {unmaskError && (
                            <div className="flex items-center gap-2 text-[11px] text-[#FF3333] bg-[#FF3333]/10 border border-[#FF3333]/30 px-3 py-2 rounded">
                                <AlertTriangle className="h-3 w-3" /> [ERROR] {unmaskError}
                            </div>
                        )}
                        {unmaskedPassword && remainingUnmasks !== null && (
                            <div className="flex flex-col gap-1 text-[11px] text-[#50A970] bg-[#50A970]/10 border border-[#50A970]/30 px-3 py-2 rounded">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-3 w-3" />
                                    [SUCCESS] Password {wasAlreadyUnmasked ? 'retrieved from cache' : 'unmasked via API'}.
                                </div>
                                <div className="pl-5 text-slate-500">
                                    Quota remaining: <span className="text-white">{remainingUnmasks}</span> {unmaskSource && `| Engine: ${unmaskSource}`}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {isPassword && !canUnmask && !unmaskError && (
                <div className="mt-2 pl-6">
                    <div className="flex items-center gap-2 text-[10px] text-orange-500 bg-orange-500/10 border border-orange-500/30 px-2 py-1.5 rounded uppercase tracking-widest">
                        <Info className="h-3 w-3 flex-shrink-0" />
                        <span>Unmasking unavailable (Missing Log ID)</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// Main Modal Component Props Interface
interface ThreatDetailModalProps {
    threat: ThreatIntelRecordDetail | null;
    threats: ThreatIntelRecordDetail[];
    isOpen: boolean;
    onClose: () => void;
    onEdit: (threat: ThreatIntelRecordDetail) => void;
    onDelete: (id: number) => void;
}

// Main Modal Component
const ThreatDetailModal: React.FC<ThreatDetailModalProps> = ({
    threat, threats, isOpen, onClose, onEdit, onDelete
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (threat && threats.length > 0) {
            const index = threats.findIndex(t => t.id === threat.id);
            setCurrentIndex(index !== -1 ? index : 0);
        }
    }, [threat, threats]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        const handleArrows = (e: KeyboardEvent) => {
            if (threats.length <= 1) return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault(); handlePrevious();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault(); handleNext();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('keydown', handleArrows);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('keydown', handleArrows);
            document.body.style.overflow = '';
        };
    }, [isOpen, threats.length, currentIndex]);

    const handlePrevious = () => {
        if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    };

    const handleNext = () => {
        if (currentIndex < threats.length - 1) setCurrentIndex(currentIndex + 1);
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    if (!isOpen || !threat) return null;

    const currentThreat = threats[currentIndex] || threat;

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 ml-40 bg-black/80 backdrop-blur-sm" onClick={handleBackdropClick}>

            <div className="relative w-full max-w-4xl max-h-[90vh] bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden animate-fade-in font-mono">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-[#1A1D24] bg-[#12151C] flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <h2 className="text-[14px] font-bold text-white tracking-widest uppercase flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#50A970]" />
                            Threat Record Intel
                        </h2>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Navigation Controls */}
                        {threats.length > 1 && (
                            <div className="flex items-center gap-2 bg-[#0A0B0E] border border-[#1A1D24] rounded-lg px-2 py-1 mr-2">
                                <button
                                    onClick={handlePrevious} disabled={currentIndex === 0}
                                    className="p-1 text-slate-500 hover:text-white disabled:opacity-30 transition-colors"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="text-[10px] text-slate-500 tracking-widest uppercase">
                                    <strong className="text-white">{currentIndex + 1}</strong> OF {threats.length}
                                </span>
                                <button
                                    onClick={handleNext} disabled={currentIndex === threats.length - 1}
                                    className="p-1 text-slate-500 hover:text-white disabled:opacity-30 transition-colors"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        )}

                        <button
                            onClick={() => onEdit(currentThreat)}
                            className="p-2 text-slate-400 hover:text-[#00FFFF] hover:bg-[#161A23] rounded-lg transition-all border border-transparent hover:border-[#1A1D24]"
                            title="Edit Threat"
                        >
                            <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => onDelete(currentThreat.id)}
                            className="p-2 text-slate-400 hover:text-[#FF3333] hover:bg-[#161A23] rounded-lg transition-all border border-transparent hover:border-[#1A1D24]"
                            title="Purge Threat"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                        <div className="w-px h-6 bg-[#1A1D24] mx-1" />
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-[#FF3333] hover:bg-[#161A23] rounded-lg transition-all border border-transparent hover:border-[#1A1D24]"
                            title="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content Wrapper (Strict Flex for Scroll boundaries) */}
                <div className="flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 min-h-0 overflow-hidden">

                        {/* Left Column - Main Details */}
                        <div className="lg:col-span-2 p-6 overflow-y-auto custom-scrollbar space-y-8 h-full">

                            {/* Primary Information */}
                            <div>
                                <h3 className="text-[11px] font-bold text-slate-300 tracking-widest uppercase mb-4 pb-2 border-b border-[#1A1D24] flex items-center gap-2">
                                    <User className="h-3.5 w-3.5 text-[#00FFFF]" /> Primary Information
                                </h3>
                                <div className="space-y-1">
                                    <DetailRow label="URL Target" value={currentThreat.url} icon={<Globe className="h-3.5 w-3.5" />} copyable fieldKey="url" />
                                    <DetailRow label="Extracted Username" value={currentThreat.username} icon={<User className="h-3.5 w-3.5" />} copyable fieldKey="username" />
                                    <DetailRow
                                        label="Obfuscated Password"
                                        value={currentThreat.password}
                                        icon={<Key className="h-3.5 w-3.5" />}
                                        copyable sensitive isPassword
                                        fieldKey="password" recordId={currentThreat.id} logId={currentThreat.log_id}
                                    />
                                </div>
                            </div>

                            {/* System Information */}
                            <div>
                                <h3 className="text-[11px] font-bold text-slate-300 tracking-widest uppercase mb-4 pb-2 border-b border-[#1A1D24] flex items-center gap-2">
                                    <Database className="h-3.5 w-3.5 text-[#50A970]" /> System Fingerprint
                                </h3>
                                <div className="space-y-1">
                                    <DetailRow label="Infected Hostname" value={currentThreat.hostname} icon={<Monitor className="h-3.5 w-3.5" />} copyable fieldKey="hostname" />
                                    <DetailRow label="Origin IP Address" value={currentThreat.ip} icon={<MapPin className="h-3.5 w-3.5" />} copyable fieldKey="ip" />
                                    <DetailRow label="Malware Execution Path" value={currentThreat.malware_path} icon={<FileText className="h-3.5 w-3.5" />} copyable fieldKey="malware_path" />
                                    <DetailRow label="Detected Antivirus" value={currentThreat.anti_virus} icon={<Shield className="h-3.5 w-3.5" />} copyable fieldKey="anti_virus" />
                                </div>
                            </div>

                            {/* Notes */}
                            {currentThreat.notes && (
                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-300 tracking-widest uppercase mb-4 pb-2 border-b border-[#1A1D24] flex items-center gap-2">
                                        <FileText className="h-3.5 w-3.5 text-orange-500" /> Analyst Notes
                                    </h3>
                                    <div className="bg-[#050505] p-4 rounded-lg border border-[#1A1D24]">
                                        <p className="text-[12px] text-slate-300 whitespace-pre-wrap leading-relaxed">
                                            {currentThreat.notes}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Tags */}
                            {currentThreat.tags && Object.keys(currentThreat.tags).length > 0 && (
                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-300 tracking-widest uppercase mb-4 pb-2 border-b border-[#1A1D24] flex items-center gap-2">
                                        <Tag className="h-3.5 w-3.5 text-purple-500" /> Custom Tags
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(currentThreat.tags).map(([key, value]) => (
                                            <span key={key} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#4da760]/10 text-[#4da760] border border-[#4da760]/30 rounded text-[10px] font-bold tracking-widest uppercase">
                                                <Tag className="h-3 w-3" />
                                                {String(value)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Metadata Sidebar */}
                        <div className="bg-[#12151C] p-6 border-t lg:border-t-0 lg:border-l border-[#1A1D24] overflow-y-auto custom-scrollbar h-full">
                            <div className="space-y-8">

                                {/* Technical Details */}
                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                                        <Server className="h-3.5 w-3.5 text-[#00FFFF]" /> Technical Details
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Record ID</div>
                                            <div className="text-[12px] text-white">#{currentThreat.id}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Log ID</div>
                                            <div className="text-[12px] text-white">{currentThreat.log_id ? `#${currentThreat.log_id}` : 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Organization</div>
                                            <div className="text-[12px] text-white">#{currentThreat.organization}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Fetch ID</div>
                                            <div className="text-[12px] text-white break-all">#{currentThreat.fetch}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Timestamps */}
                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                                        <Clock className="h-3.5 w-3.5 text-purple-500" /> Timestamps
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Log Date</div>
                                            <div className="text-[12px] text-slate-300">{formatDate(currentThreat.log_date)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Saved At</div>
                                            <div className="text-[12px] text-slate-300">{formatDate(currentThreat.saved_at)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Updated At</div>
                                            <div className="text-[12px] text-slate-300">{formatDate(currentThreat.updated_at)}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Classification */}
                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                                        <Hash className="h-3.5 w-3.5 text-orange-500" /> Classification
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Severity</div>
                                            <SeverityBadge severity={currentThreat.severity} />
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Status</div>
                                            <StatusBadge status={currentThreat.status} />
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Data Type</div>
                                            <DataTypeBadge dataType={currentThreat.data_type} />
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Source Engine</div>
                                            <SourceBadge source={currentThreat.source} />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThreatDetailModal;